/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiDescriptionList,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiFormRow,
  EuiPanel,
  EuiRadioGroup,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import useOrgDomainChange from '@modules/billing-lib/mutations/useOrgDomainChange'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import {
  isEligibleForRetroactiveBilling,
  isEligibleForMPConversion,
  isEligibleForDirectConversion,
} from '@modules/billing-lib/billingDetails/utils'
import {
  invalidateGetOrganizationQuery,
  invalidateGetSaasUserQuery,
} from '@modules/profile-lib/hooks'

import AwsProviderForm from './AwsProviderForm'
import GcpProviderForm from './GcpProviderForm'
import DirectProviderForm from './DirectProviderForm'
import { Provider } from './types'
import { isProviderFormValid, mapStateToApiPayload } from './utils'
import { DomainChangeErrorCallOut } from './DomainChangeErrorCallOut'

import type { AwsState } from './AwsProviderForm'
import type { GcpState } from './GcpProviderForm'
import type { DirectState } from './DirectProviderForm'

interface Props {
  organizationId: string
  onClose: () => void
}

/* ==========================================================================
   COMPONENT DEFINITION
   ========================================================================== */

const ChangeDomainFlyout = ({ organizationId, onClose }: Props) => {
  // --------------------------------------------------------------------------
  // State Management
  // --------------------------------------------------------------------------

  const [provider, setProvider] = useState<Provider>(Provider.AWS)
  const [isDirty, setIsDirty] = useState(false)

  // State: AWS
  const [awsState, setAwsState] = useState<AwsState>({
    account_id: '',
    backdate_on_consumption: false,
  })
  const [awsTouched, setAwsTouched] = useState<Partial<Record<keyof AwsState, boolean>>>({})

  // State: GCP
  const [gcpState, setGcpState] = useState<GcpState>({
    billing_account_id: '',
    backdate_on_consumption: false,
  })
  const [gcpTouched, setGcpTouched] = useState<Partial<Record<keyof GcpState, boolean>>>({})

  // State: Direct
  const [directState, setDirectState] = useState<DirectState>({
    start_date: '',
    activation_code: '',
    level: '',
  })
  const [directTouched, setDirectTouched] = useState<Partial<Record<keyof DirectState, boolean>>>(
    {},
  )

  // --------------------------------------------------------------------------
  // Hooks & Utilities
  // --------------------------------------------------------------------------

  const billingDetails = useBillingDetails()
  const orgDomainChangeMutation = useOrgDomainChange()
  const flyoutTitleId = useGeneratedHtmlId({ prefix: 'ChangeDomainFlyout' })

  const options = [
    {
      id: Provider.AWS,
      label: 'AWS Marketplace',
      disabled: !isEligibleForMPConversion(billingDetails.data),
      'data-test-id': 'aws-selector',
    },
    {
      id: Provider.GCP,
      label: 'GCP Marketplace',
      disabled: !isEligibleForMPConversion(billingDetails.data),
      'data-test-id': 'gcp-selector',
    },
    {
      id: Provider.DIRECT,
      label: 'Elastic Direct',
      disabled: !isEligibleForDirectConversion(billingDetails.data),
      'data-test-id': 'direct-selector',
    },
  ]
  const enabledIds = options
    .filter((o) => !o.disabled)
    .map((o) => o.id)
    .join(',')

  React.useEffect(() => {
    if (!billingDetails.data) {
      return
    }

    const activeOptions = options.filter((opt) => !opt.disabled)

    // AUTO-SELECT LOGIC:
    // If nothing is selected OR the current selection is actually disabled by data
    const currentIsDisabled = options.find((o) => o.id === provider)?.disabled

    if (activeOptions.length > 0 && (!provider || currentIsDisabled)) {
      setProvider(activeOptions[0]?.id as Provider)
    }
  }, [enabledIds, billingDetails.data, provider])

  // --------------------------------------------------------------------------
  // Handlers
  // --------------------------------------------------------------------------

  const handleProviderChange = (id: string) => {
    setIsDirty(true)
    setProvider(id as Provider)
  }

  // Generic handler wrappers to set dirty state on input
  const handleAwsChange = (updates: Partial<AwsState>) => {
    setIsDirty(true)
    setAwsState((prev) => ({ ...prev, ...updates }))
  }

  const handleGcpChange = (updates: Partial<GcpState>) => {
    setIsDirty(true)
    setGcpState((prev) => ({ ...prev, ...updates }))
  }

  const handleDirectChange = (updates: Partial<DirectState>) => {
    setIsDirty(true)
    setDirectState((prev) => ({ ...prev, ...updates }))
  }

  const isFormValid = isProviderFormValid({ provider, awsState, gcpState, directState })

  const onSubmit = () => {
    if (!isFormValid) {
      return
    }

    setIsDirty(false)

    const payload = mapStateToApiPayload(provider, {
      aws: awsState,
      gcp: gcpState,
      direct: directState,
    })

    orgDomainChangeMutation.mutate(
      { pathParameters: { organization_id: organizationId }, body: payload },
      {
        onSuccess: () => {
          billingDetails.refetch()
          invalidateGetOrganizationQuery()
          invalidateGetSaasUserQuery()
          onClose()
        },
      },
    )
  }

  // --------------------------------------------------------------------------
  // Main Render
  // --------------------------------------------------------------------------
  return (
    <EuiFlyout size='s' ownFocus={true} onClose={onClose} aria-labelledby={flyoutTitleId}>
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id={flyoutTitleId}>
            <FormattedMessage
              id='change-domain-flyout.title'
              defaultMessage='Change Payment Provider'
            />
          </h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiText>
          <p>
            <FormattedMessage
              id='change-domain-flyout.description'
              defaultMessage='Transfer billing responsibility to a different provider.'
            />
          </p>
        </EuiText>

        <EuiSpacer size='m' />

        <EuiDescriptionList
          type='inline'
          listItems={[
            {
              title: (
                <FormattedMessage
                  id='change-domain-flyout.organization-id'
                  defaultMessage='Organization ID'
                />
              ),
              description: organizationId,
            },
          ]}
        />

        <EuiSpacer size='l' />

        <EuiTitle size='xs'>
          <h3>
            <FormattedMessage
              id='change-domain.target-channel.title'
              defaultMessage='Target Payment Provider'
            />
          </h3>
        </EuiTitle>
        <EuiText size='s' color='subdued'>
          <p>
            <FormattedMessage
              id='change-domain.target-channel.help'
              defaultMessage={
                'Select the entity responsible for invoicing this organization. If you select a CSP, usage is ' +
                "reported to them and invoiced via their marketplace. If you select 'Direct', Elastic becomes the " +
                " merchant of record and will charge the customer's credit card directly."
              }
            />
          </p>
        </EuiText>
        <EuiSpacer size='s' />

        <EuiForm>
          <EuiFormRow fullWidth={true}>
            <EuiRadioGroup
              options={options} // Use the derived options array
              idSelected={provider ?? ''}
              onChange={handleProviderChange}
              name='provider-select'
            />
          </EuiFormRow>

          <EuiSpacer size='m' />

          {/* Configuration Panel - Conditional Render based on Provider */}
          <EuiPanel color='subdued' hasShadow={false} paddingSize='m'>
            {provider === Provider.AWS && (
              <AwsProviderForm
                values={awsState}
                touched={awsTouched}
                isDisabled={orgDomainChangeMutation.isLoading}
                onChange={handleAwsChange}
                onBlur={(field) => setAwsTouched((prev) => ({ ...prev, [field]: true }))}
                showBackdate={
                  billingDetails.data ? isEligibleForRetroactiveBilling(billingDetails.data) : false
                }
              />
            )}

            {provider === Provider.GCP && (
              <GcpProviderForm
                values={gcpState}
                touched={gcpTouched}
                isDisabled={orgDomainChangeMutation.isLoading}
                onChange={handleGcpChange}
                onBlur={(field) => setGcpTouched((prev) => ({ ...prev, [field]: true }))}
                showBackdate={
                  billingDetails.data ? isEligibleForRetroactiveBilling(billingDetails.data) : false
                }
              />
            )}

            {provider === Provider.DIRECT && (
              <DirectProviderForm
                values={directState}
                touched={directTouched}
                isDisabled={orgDomainChangeMutation.isLoading}
                onChange={handleDirectChange}
                onBlur={(field) => setDirectTouched((prev) => ({ ...prev, [field]: true }))}
              />
            )}
          </EuiPanel>
        </EuiForm>

        {/* Status Messaging */}
        <EuiSpacer size='m' />
        {orgDomainChangeMutation.isSuccess && (
          <EuiCallOut
            announceOnMount={true}
            title='Migration Successful'
            color='success'
            iconType='check'
          />
        )}
        {orgDomainChangeMutation.isError && !isDirty && (
          <DomainChangeErrorCallOut error={orgDomainChangeMutation.error} />
        )}
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty
              data-test-id='cancel-button'
              iconType='cross'
              onClick={onClose}
              flush='left'
            >
              <FormattedMessage id='change-domain.cancel-conversion' defaultMessage='Cancel' />
            </EuiButtonEmpty>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton
              data-test-id='change-domain-button'
              fill={true}
              onClick={onSubmit}
              isLoading={orgDomainChangeMutation.isLoading}
              disabled={!isFormValid || orgDomainChangeMutation.isSuccess}
            >
              <FormattedMessage id='change-domain.confirm-conversion' defaultMessage='Confirm' />
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default ChangeDomainFlyout
